import { useCallback, useEffect, useState } from 'react';
import { getStoreEmailSettings, saveStoreEmailSettings } from '../../../utils/queries';
import { useCan } from '../../../hooks/useCan';
import { PLATFORMADMIN_ROLES } from '../../../components/navConfig';
import EmailTagInput from '../../../components/EmailTagInput';
import Tooltip from '../../../components/Tooltip';
import { splitEmailList } from '../../../utils/validation';

const NOTIFICATIONS = [
	{
		key: 'orderConfirmationEnabled',
		label: 'Order confirmation emails',
		description: 'Email the customer a receipt when their order is placed.',
	},
	{
		key: 'orderShippedEnabled',
		label: 'Order shipped emails',
		description: 'Email the customer a shipping update when their order ships.',
	},
	{
		key: 'orderCancelledEnabled',
		label: 'Order cancelled emails',
		description: 'Email the customer when their order is cancelled.',
	},
	// Opt-in (off unless explicitly enabled) -- the API defaults these columns
	// to false, unlike the other customer notifications.
	{
		key: 'orderOnHoldEnabled',
		label: 'Order on hold emails',
		description: 'Email the customer when their order is placed on hold.',
		defaultEnabled: false,
	},
	{
		key: 'orderDelayedEnabled',
		label: 'Order delayed emails',
		description:
			"Email the customer once if an open order hasn't shipped within the delayed-shipment threshold",
		defaultEnabled: false,
		tooltip:
			'The number of days is set by "Delayed shipment after (days)" under Settings > General (default 3).',
	},
	{
		key: 'orderApprovalDecisionEnabled',
		label: 'Order approved emails',
		description:
			'Send e-mail notification to customer that approver has updated this order.',
	},
];

const INTERNAL_NOTIFICATIONS = [
	{
		key: 'internalNewOrderNotificationEnabled',
		label: 'Receive email notifications when new orders are placed',
	},
	{
		key: 'lowStock',
		label: 'Receive email notifications when product stock falls below 10 units',
		comingSoon: true,
	},
];

export default function NotificationsTab({ storeId }) {
	const canEdit = useCan(PLATFORMADMIN_ROLES);
	const [settings, setSettings] = useState(null);
	const [loading, setLoading] = useState(true);
	const [savingKey, setSavingKey] = useState(null);
	const [error, setError] = useState(null);

	const [recipients, setRecipients] = useState([]);
	const [savingRecipients, setSavingRecipients] = useState(false);

	const load = useCallback(() => getStoreEmailSettings(storeId), [storeId]);

	useEffect(() => {
		setLoading(true);
		load().then((result) => {
			if (result?.success) {
				setSettings(result.data || {});
				setRecipients(splitEmailList(result.data?.internalNotificationEmails).valid);
			} else {
				setError(result?.error || 'Failed to load notification settings.');
			}
			setLoading(false);
		});
	}, [load]);

	const toggle = async (key, nextChecked) => {
		const previous = settings;
		setSettings((current) => ({ ...current, [key]: nextChecked }));
		setSavingKey(key);
		setError(null);

		const result = await saveStoreEmailSettings(storeId, { [key]: nextChecked });
		setSavingKey(null);

		if (!result?.success) {
			setSettings(previous);
			setError(result?.error || 'Failed to save notification setting.');
		}
	};

	const saveRecipients = async (next) => {
		const previous = recipients;
		setRecipients(next);
		setSavingRecipients(true);
		setError(null);

		const result = await saveStoreEmailSettings(storeId, { internalNotificationEmails: next.join(', ') });
		setSavingRecipients(false);

		if (!result?.success) {
			setRecipients(previous);
			setError(result?.error || 'Failed to save notification recipients.');
			return;
		}
		setSettings((current) => ({ ...current, internalNotificationEmails: next.join(', ') }));
	};

	if (loading) {
		return <div className='text-sm text-gray-400'>Loading...</div>;
	}

	return (
		<div>
			<p className='text-sm font-medium text-gray-900'>Customer Notifications</p>
			<p className='text-sm text-gray-500 mb-4'>Configure which email notifications customers will receive</p>

			{error && <p className='text-sm text-red-600 mb-4'>{error}</p>}

			<div className='border p-4 divide-y divide-gray-100'>
				{NOTIFICATIONS.map(({ key, label, description, defaultEnabled = true, tooltip }) => {
					const checked = settings?.[key] ?? defaultEnabled;
					const inputId = `notification-${key}`;
					return (
						<label
							key={key}
							htmlFor={inputId}
							className={`flex items-start gap-3 py-4 ${savingKey === key || !canEdit ? '' : 'cursor-pointer'}`}
						>
							<input
								id={inputId}
								type='checkbox'
								className='mt-1 h-4 w-4 rounded border-gray-300 accent-black focus:ring-black disabled:opacity-50'
								checked={checked}
								disabled={savingKey === key || !canEdit}
								onChange={(e) => toggle(key, e.target.checked)}
							/>
							<div>
								<div className='flex items-center gap-1.5'>
									<p className='text-sm font-medium text-gray-900'>{label}</p>
									<Tooltip content={tooltip} />
								</div>
								<p className='text-sm text-gray-400'>{description}</p>
							</div>
						</label>
					);
				})}
			</div>

			<div className='mt-8 pt-6 border-t border-gray-200'>
				<p className='text-sm font-medium text-gray-900'>Internal Notifications</p>
				<p className='text-sm text-gray-500 mb-4'>Configure which email notifications internal team members receive</p>

				<div className='border p-4 divide-y divide-gray-100'>
					{INTERNAL_NOTIFICATIONS.map(({ key, label, comingSoon }) => {
						if (comingSoon) {
							return (
								<label key={key} className='flex items-start gap-3 py-4 opacity-50'>
									<input
										type='checkbox'
										className='mt-1 h-4 w-4 rounded border-gray-300 accent-black'
										checked={false}
										disabled
										readOnly
									/>
									<div>
										<p className='text-sm text-gray-700'>{label}</p>
										<p className='text-xs text-gray-400'>Coming soon — not yet functional.</p>
									</div>
								</label>
							);
						}

						const checked = settings?.[key] === true;
						const inputId = `notification-${key}`;
						return (
							<div key={key} className='py-4'>
								<label
									htmlFor={inputId}
									className={`flex items-start gap-3 ${savingKey === key || !canEdit ? '' : 'cursor-pointer'}`}
								>
									<input
										id={inputId}
										type='checkbox'
										className='mt-1 h-4 w-4 rounded border-gray-300 accent-black focus:ring-black disabled:opacity-50'
										checked={checked}
										disabled={savingKey === key || !canEdit}
										onChange={(e) => toggle(key, e.target.checked)}
									/>
									<p className='text-sm font-medium text-gray-900'>{label}</p>
								</label>

								{checked && (
									<div className='mt-3 ml-7 max-w-lg'>
										<p className='text-sm text-gray-500 mb-1'>Send these emails to:</p>
										<EmailTagInput value={recipients} onChange={canEdit ? saveRecipients : () => {}} />
										{savingRecipients && <p className='text-xs text-gray-400 mt-1'>Saving…</p>}
										{!savingRecipients && recipients.length === 0 && (
											<p className='text-xs text-gray-400 mt-1'>
												Add at least one address to start receiving these emails.
											</p>
										)}
									</div>
								)}
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
}
